import { tx } from "@/i18n/tx";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Share2, Copy, Check } from "lucide-react";

// options: [{ id, label, text }] — testo già nella lingua selezionata.
// url: link pubblico definitivo da allegare (senza link di anteprima).
export function ShareSheet({ options, url, onClose }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(() => {
    const init = {};
    options.forEach((o, i) => { init[o.id] = i === 0; });
    return init;
  });

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const composed = useMemo(() => {
    const chosen = options.filter((o) => selected[o.id] && o.text && o.text.trim());
    const blocks = chosen.map((o) => `${o.label}\n${o.text.trim()}`);
    const body = blocks.join("\n\n");
    return body ? `${body}\n\n— Ascolta Anima\n${url}` : "";
  }, [options, selected, url]);

  const anySelected = options.some((o) => selected[o.id]);

  const doShare = async () => {
    if (!composed) { toast.error(t("share.selectOne")); return; }
    try {
      if (navigator.share) {
        await navigator.share({ title: tx("Ascolta Anima"), text: composed });
        onClose();
      } else {
        await navigator.clipboard.writeText(composed);
        toast.success(t("share.copied"));
      }
    } catch (err) { console.debug(tx("Share dismissed:"), err?.message || err); }
  };

  const doCopy = async () => {
    if (!composed) { toast.error(t("share.selectOne")); return; }
    try {
      await navigator.clipboard.writeText(composed);
      toast.success(t("share.copied"));
    } catch (err) { console.debug(tx("Copy failed:"), err?.message || err); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-anima-ink/35 backdrop-blur-sm px-4 py-4"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-anima-ivory rounded-3xl p-6 max-h-[90vh] overflow-y-auto no-scrollbar"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="font-serif text-2xl text-anima-ink">{t("share.title")}</p>
          <button onClick={onClose} data-testid="share-close"><X size={20} className="text-anima-muted" /></button>
        </div>
        <p className="text-sm text-anima-muted mb-4">{t("share.subtitle")}</p>

        <div className="space-y-2.5">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              aria-pressed={!!selected[o.id]}
              data-testid={`share-opt-${o.id}`}
              className="w-full flex items-center gap-3 text-left rounded-2xl border border-anima-ink/10 bg-white/55 px-4 py-3"
            >
              <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${selected[o.id] ? "bg-anima-sagedeep border-anima-sagedeep" : "border-anima-ink/25"}`}>
                {selected[o.id] && <Check size={13} className="text-white" />}
              </span>
              <span className="text-[15px] text-anima-ink">{o.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">{t("share.preview")}</p>
          <pre
            data-testid="share-preview"
            className="whitespace-pre-wrap break-words rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3 text-[13px] text-anima-ink/85 leading-relaxed font-sans min-h-[60px]"
          >{composed || "…"}</pre>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={doCopy} disabled={!anySelected}
            data-testid="share-copy-btn"
            className="flex-1 rounded-full border border-anima-ink/15 text-anima-ink py-3.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Copy size={16} /> {t("share.copy")}
          </button>
          <button
            onClick={doShare} disabled={!anySelected}
            data-testid="share-send-btn"
            className="flex-1 rounded-full bg-anima-sagedeep text-anima-ivory py-3.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Share2 size={16} /> {t("share.action")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
